import { VerticalJustification, WallVerticalDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { DEFAULT_TRIM_PROFILE_DESCRIPTOR } from "building-types/profiles/defaultBoardTrimProfiles";
import { ProfileExtrusionType } from "building-types/profiles/profileTypesEnum";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { BellyBandBoard } from "building-types/roofs/roofTrimTypes";
import { bellyBandBoardNew } from "building-types/roofs/roofTrimTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type { EvaluatedRoofEdgeOverrides } from "building-types/settings/roofTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import { EaveType } from "building-types/settings/settingsTypesEnum";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import { assertIsNotNull } from "common/utils/assertOfType";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { faceArrayViewEntityNew } from "doc/utils/viewEntityTools";
import { aabb2Height } from "geom/aabb2Types";
import { type Face } from "geom/brep";
import { tagFaces } from "geom/brep";
import { clipFaces, clipRail3, clipRails3, clippingNodeNew } from "geom/bspClipping";
import type { NodeBSP } from "geom/bspTypes";
import { DIST_EPSILON } from "geom/core";
import { extrudeFaceAlongPolyLine3 } from "geom/extrude";
import { planeNewXY, planeNormal, planeProjectPolyLine3, polyLine2OnPlane } from "geom/plane";
import { polyLine2Extend, polyLine2AABB2, polyLine2Copy, polyLine2Move, polyLine2Simplify } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import {
  polyLine3StartPoint,
  type PolyLine3,
  polyLine3EndPoint,
  polyLine3ExtendStart,
  polyLine3ExtendEnd,
  polyLine3Move,
} from "geom/polyLine3";
import { vec3DistSquared } from "geom/vec3";
import { pushMissingProfileReferenceIssue } from "../../elements/profileMissingReference";
import { getActiveProfileDescriptor, getProfileExtrusionDescriptorProfile } from "../../elements/profileTools";
import { getDefaultJustification } from "../../utils/facade";
import { resolvedRoofSideParams } from "./roofSideOverridesUtility";

interface BuildBellyBandBoardOptions {
  readonly bellyBandBoard: BellyBandBoard | undefined;
  readonly profileSource: ActiveProfileSource;
  readonly clipContext: MultiGableRoofClipContext;
  readonly wallOverlapForFriezeBoardBSPs: NodeBSP[];
  readonly gableWallOverlapBSPs: NodeBSP[];
  readonly planeOffset: number;
  readonly bellyBandBoards?: FaceArrayViewEntity[];
  readonly issues?: BuildingGenerationIssue[];
  readonly handle?: DocEntityHandle;
}

const tagBellyBandBoard = { type: "CONDITION", condition: "BELLY_BAND_BOARD" };

export function buildBellyBandBoard({
  bellyBandBoard,
  profileSource,
  clipContext,
  wallOverlapForFriezeBoardBSPs,
  gableWallOverlapBSPs,
  planeOffset,
  bellyBandBoards = [],
  issues,
  handle,
}: BuildBellyBandBoardOptions): FaceArrayViewEntity[] {
  if (!bellyBandBoard) {
    return bellyBandBoards;
  }

  const clipBellyBandRail = (rail: PolyLine3, bsps: NodeBSP[], clippedRails: PolyLine3[] = []) => {
    for (const bsp of bsps) {
      clipRail3({
        rail,
        options: {
          clippingNodes: [clippingNodeNew(bsp, true)],
          epsilon: MULTIGABLE_BSP_EPSILON,
        },
        clippedRails,
      });
    }

    return clippedRails;
  };

  const { insideBSP, discardBSP } = clipContext;
  const {
    profileId,
    material,
    justification,
    rail,
    plane,
    eaveType,
    startOverhang,
    endOverhang,
    startBaseHeight,
    endBaseHeight,
  } = bellyBandBoard;

  const descriptor = getActiveProfileDescriptor(profileSource, profileId, DEFAULT_TRIM_PROFILE_DESCRIPTOR);
  if (!descriptor) {
    pushMissingProfileReferenceIssue(issues, profileSource, profileId, handle, "gableBellyBandBoard");
    return bellyBandBoards;
  }
  const profile = polyLine2Simplify(polyLine2Copy(getProfileExtrusionDescriptorProfile(descriptor)));
  const profileAABB = polyLine2AABB2(profile);

  polyLine2Move(profile, -profileAABB.maxX, -profileAABB.minY);

  if (justification === VerticalJustification.TOP_DOWN) {
    polyLine2Move(profile, 0, -aabb2Height(profileAABB));
  }

  let trimmedBellyBandBoardRails: PolyLine3[] = [];
  clipBellyBandRail(rail, wallOverlapForFriezeBoardBSPs, trimmedBellyBandBoardRails);
  clipBellyBandRail(rail, gableWallOverlapBSPs, trimmedBellyBandBoardRails);

  trimmedBellyBandBoardRails = clipRails3({
    rails: trimmedBellyBandBoardRails,
    options: {
      clippingNodes: [clippingNodeNew(insideBSP, true)],
      epsilon: MULTIGABLE_BSP_EPSILON,
    },
  });

  // extend the belly band board to the edge of the roof for boxed open eaves
  if (eaveType === EaveType.BOXED_OPEN) {
    const [startX, startY, startZ] = polyLine3StartPoint(rail);
    const [endX, endY, endZ] = polyLine3EndPoint(rail);

    for (const trimmedBellyBandBoardRail of trimmedBellyBandBoardRails) {
      const [myStartX, myStartY, myStartZ] = polyLine3StartPoint(trimmedBellyBandBoardRail);
      const [myEndX, myEndY, myEndZ] = polyLine3EndPoint(trimmedBellyBandBoardRail);

      const isStart = vec3DistSquared(startX, startY, startZ, myStartX, myStartY, myStartZ) < 1;
      const isEnd = vec3DistSquared(endX, endY, endZ, myEndX, myEndY, myEndZ) < 1;

      if (isStart && myStartZ - startBaseHeight > -DIST_EPSILON) {
        polyLine3ExtendStart(trimmedBellyBandBoardRail, startOverhang);
      }

      if (isEnd && myEndZ - endBaseHeight > -DIST_EPSILON) {
        polyLine3ExtendEnd(trimmedBellyBandBoardRail, endOverhang);
      }
    }
  }

  const faces: Face[] = [];
  const [nX, nY, nZ] = planeNormal(plane);
  for (let trimmedBellyBandBoardRail of trimmedBellyBandBoardRails) {
    trimmedBellyBandBoardRail = polyLine3Move(
      trimmedBellyBandBoardRail,
      nX * planeOffset,
      nY * planeOffset,
      nZ * planeOffset,
    );

    const brep = extrudeFaceAlongPolyLine3([profile], trimmedBellyBandBoardRail, 0, 0, 1);
    clipFaces({
      faces: brep.faces,
      options: {
        clippingNodes: [clippingNodeNew(discardBSP, true)],
        epsilon: MULTIGABLE_BSP_EPSILON,
      },
      clippedFaces: faces,
    });
  }

  bellyBandBoards.push(faceArrayViewEntityNew(tagFaces(faces, tagBellyBandBoard), descriptor, material));

  return bellyBandBoards;
}

export function buildBellyBandBoardRail(
  baseRail: PolyLine2,
  face: Face | null,
  roofSettings: RoofSettings,
  topOfWallHeight: number,
  fasciaHeight: number,
  eaveType: EaveType,
  startOverhang: number,
  endOverhang: number,
  startBaseHeight: number,
  endBaseHeight: number,
  roofSideOverride: EvaluatedRoofEdgeOverrides | undefined,
  isEnd: boolean,
): BellyBandBoard | undefined {
  const roofSide = isEnd ? RoofSide.END : RoofSide.START;
  const {
    gableBellyBandBoard = ProfileExtrusionType.NONE,
    gableBellyBandBoardMaterial = "trimExterior0",
    gableBellyBandBoardAnchor = WallVerticalDatumAnchor.GABLE_END,
    gableBellyBandBoardJustification = getDefaultJustification(gableBellyBandBoardAnchor),
    gableBellyBandBoardOffset = 0,
  } = resolvedRoofSideParams(roofSettings, roofSide, false, false, false, roofSideOverride);

  if (gableBellyBandBoard === ProfileExtrusionType.NONE) {
    return undefined;
  }
  assertIsNotNull(face);
  const { plane } = face;

  let datumHeight = 0;
  switch (gableBellyBandBoardAnchor) {
    case WallVerticalDatumAnchor.GABLE_END:
      datumHeight = fasciaHeight;
      break;
    case WallVerticalDatumAnchor.BASE_WALL:
      datumHeight = topOfWallHeight;
      break;
  }
  const offsetSign = gableBellyBandBoardJustification === VerticalJustification.BOTTOM_UP ? 1 : -1;
  const finalOffset = datumHeight + offsetSign * gableBellyBandBoardOffset;

  if (finalOffset <= datumHeight && offsetSign === -1) {
    polyLine2Extend(baseRail, startOverhang, endOverhang);
  }

  const baseRail3 = polyLine2OnPlane(planeNewXY(0, 0, finalOffset), baseRail);

  const rail3 = polyLine2OnPlane(plane, planeProjectPolyLine3(plane, baseRail3));

  return bellyBandBoardNew(
    plane,
    rail3,
    gableBellyBandBoard,
    gableBellyBandBoardMaterial,
    gableBellyBandBoardJustification,
    eaveType,
    startOverhang,
    endOverhang,
    startBaseHeight,
    endBaseHeight,
    roofSide,
  );
}
